import {
  forwardRef,
  FunctionComponent,
  MutableRefObject,
  PropsWithoutRef,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  Fragment,
  useCallback,
  MouseEvent
} from 'react';

import { ForwardProps } from '../../types';
import Drawer from '../Drawer';
import {
  useAfterInitialEffect,
  useConsolidatedRef,
  useItemIntersection,
  useDirection
} from '../../hooks';
import Progress from '../Progress';
import EmptyState from '../EmptyState';

import type {
  AcceptedMouseEventElement,
  MenuListProps,
  ParentMenuItemProps,
  MenuItemProps
} from './Menu.types';
import MenuContext from './Menu.context';
import MenuListHeader from './MenuListHeader';
import MenuItem from './MenuItem';
import helpers from './helpers';
import {
  StyledLoadingItem,
  StyledMenuList,
  StyledMenuListContainer,
  StyledSeparator
} from './Menu.styles';
import { resizeRootEl } from './NavItemsList';
import MenuGroup from './MenuGroup';

const setParentDisabled = (fieldset: HTMLFieldSetElement, bool: boolean) => {
  fieldset.disabled = bool;
  const legendButton = fieldset.querySelector<HTMLButtonElement>('legend button');
  if (legendButton) {
    legendButton.disabled = bool;
  }
};

const MenuList: FunctionComponent<MenuListProps & ForwardProps> = forwardRef(
  ({ items, parent }: PropsWithoutRef<MenuListProps>, ref: MenuListProps['ref']) => {
    const menuListWrapperRef: MutableRefObject<HTMLElement | null> = useRef(null);
    const selfRef = useConsolidatedRef(ref);
    const ulRef = useRef<HTMLUListElement>(null);
    const returnFocusRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
    const [open, setOpen] = useState(!parent);
    const [expandedItem, setExpandedItem] = useState<ParentMenuItemProps>();
    const {
      scrollAt,
      loadMore,
      loading,
      emptyText,
      currentItemId,
      onItemExpand,
      focusControl,
      updateActiveDescendants,
      setFocusReturnEl,
      onItemCollapse
    } = useContext(MenuContext);
    const { end } = useDirection();

    useItemIntersection(
      ulRef,
      items.length - 1,
      () => {
        if (!loading) loadMore?.(parent?.item?.id);
      },
      ':scope > li'
    );

    useLayoutEffect(() => {
      if (!selfRef.current || expandedItem) return;
      menuListWrapperRef.current = selfRef.current.parentElement;
      resizeRootEl(
        selfRef.current,
        menuListWrapperRef.current as HTMLElement,
        items.length,
        scrollAt
      );
    });

    useEffect(() => {
      if (parent) setOpen(true);
    }, []);

    useAfterInitialEffect(() => {
      if (expandedItem) {
        setExpandedItem(items.find(item => item.id === expandedItem.id) as ParentMenuItemProps); // FIXME
      }
    }, [expandedItem, items]);

    useAfterInitialEffect(() => {
      if (currentItemId) {
        // if there is ancestor item of the controlled item, set it to expanded.
        const ancestor = items.find(
          item => item.items?.length && helpers.getItem(item.items, currentItemId) !== undefined
        );

        if (ancestor) {
          setExpandedItem(ancestor as ParentMenuItemProps);
          updateActiveDescendants({ preventScroll: true });
        }
      }
    }, [currentItemId, items, parent]);

    const onExpandCallback = useCallback(
      (id: MenuItemProps['id'], e: MouseEvent<AcceptedMouseEventElement>) => {
        const item = helpers.getItem(items, id);
        returnFocusRef.current = e.currentTarget as HTMLButtonElement;
        setExpandedItem(item as ParentMenuItemProps); // FIXME
        item?.onExpand?.(id, e);
        onItemExpand?.(id, e);
        updateActiveDescendants({ preventScroll: true });
      },
      [items]
    );

    const listContent = useMemo(() => {
      if (items.length) {
        return items.map((item, index) => {
          return helpers.isItem(item) ? (
            <MenuItem
              {...item}
              key={item.id}
              onExpand={item.items ? onExpandCallback : undefined}
            />
          ) : (
            <Fragment key={item.id}>
              <MenuGroup {...item} />
              {items[index + 1] && helpers.isItem(items[index + 1]) && (
                <StyledSeparator role='separator' />
              )}
            </Fragment>
          );
        });
      }

      if (!loading) {
        return <EmptyState message={emptyText} forwardedAs='li' />;
      }

      return null;
    }, [items, loading, emptyText]);

    const list = (
      <StyledMenuList ref={ulRef} role='menu'>
        {listContent}
        {loading && !expandedItem && (
          <StyledLoadingItem>
            <Progress placement='local' />
          </StyledLoadingItem>
        )}
      </StyledMenuList>
    );

    return (
      <>
        <Drawer
          aria-hidden={!!expandedItem}
          as={StyledMenuListContainer}
          ref={selfRef}
          open={open}
          style={{ opacity: expandedItem ? 0 : 1 }}
          disabled={!!expandedItem}
          placement={end}
          onBeforeOpen={() => {
            if (parent) {
              parent.el.style.opacity = '0';
            }

            if (selfRef.current) {
              selfRef.current.style.opacity = '1';
            }
          }}
          onAfterOpen={() => {
            if (parent) {
              setParentDisabled(parent.el, true);
            }
          }}
          onBeforeClose={() => {
            if (parent && menuListWrapperRef.current) {
              setParentDisabled(parent.el, false);
              parent.el.style.opacity = '1';
              resizeRootEl(
                parent.el,
                menuListWrapperRef.current,
                parent.siblingItems.length,
                scrollAt
              );
            }

            if (selfRef.current) {
              selfRef.current.style.opacity = '0';
            }
          }}
          onAfterClose={() => {
            if (parent) {
              focusControl?.focus();

              const expandBtn = parent.returnFocusRef.current;
              parent.setExpandedItem(undefined);

              if (expandBtn) {
                setFocusReturnEl(expandBtn?.closest('li'));
              }

              onItemCollapse?.(parent.item.id);
              updateActiveDescendants();
            }
          }}
        >
          {parent && (
            <MenuListHeader
              text={parent.item.primary}
              onClick={() => {
                setOpen(false);
              }}
            />
          )}

          {list}
        </Drawer>

        {expandedItem && selfRef.current && (
          <MenuList
            items={expandedItem.items}
            parent={{
              el: selfRef.current,
              item: expandedItem,
              siblingItems: items,
              setExpandedItem,
              returnFocusRef
            }}
          />
        )}
      </>
    );
  }
);

export default MenuList;
